import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { useHistory, useLocation } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { auth, db, storage } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import { gerarSequencial } from "../Utils/sequencial";

import RelatorioCaracterizacaoPrint from "./RelatorioCaracterizacaoPrint";
import { COLECAO as COLECAO_GRANULOMETRIA_ASFALTO } from "../CadastroGranulometriaAsfalto/CadastroGranulometriaAsfalto";

const TIPO_LAUDO_CARACTERIZACAO = "Relatório de ensaio de agregados";

function nomeSeguroArquivo(valor) {
  return String(valor || "relatorio")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

async function gerarPdfRelatorioCaracterizacao() {
  if (document.fonts?.ready) await document.fonts.ready;

  const paginas = Array.from(
    document.querySelectorAll(
      ".relatorio-caracterizacao-geral-print .pagina-preview-real, .pagina-anexo-caracterizacao .folha-rosto-anexo, .relatorio-anexo-completo .pagina"
    )
  );

  if (!paginas.some((pagina) => pagina.classList.contains("pagina-preview-real"))) {
    throw new Error("As páginas do relatório ainda não estão prontas. Aguarde a prévia carregar e tente salvar novamente.");
  }

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  let quantidadePaginas = 0;

  const adicionarPagina = (canvas) => {
    if (quantidadePaginas > 0) pdf.addPage("a4", "portrait");
    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.92),
      "JPEG",
      0,
      0,
      210,
      297,
      undefined,
      "FAST"
    );
    quantidadePaginas += 1;
  };

  for (const pagina of paginas) {
    const canvas = await html2canvas(pagina, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      logging: false,
      imageTimeout: 15000,
      onclone: (documentoClonado) => {
        documentoClonado
          .querySelector(".relatorio-caracterizacao-geral-print")
          ?.classList.add("captura-pdf");
      },
    });

    if (!pagina.classList.contains("pagina-conteudo-continuo")) {
      adicionarPagina(canvas);
      continue;
    }

    const alturaPaginaCanvas = Math.round(canvas.width * (297 / 210));
    for (let origemY = 0; origemY < canvas.height; origemY += alturaPaginaCanvas) {
      const fatia = document.createElement("canvas");
      fatia.width = canvas.width;
      fatia.height = alturaPaginaCanvas;
      const contexto = fatia.getContext("2d");
      contexto.fillStyle = "#ffffff";
      contexto.fillRect(0, 0, fatia.width, fatia.height);
      contexto.drawImage(
        canvas,
        0,
        origemY,
        canvas.width,
        Math.min(alturaPaginaCanvas, canvas.height - origemY),
        0,
        0,
        canvas.width,
        Math.min(alturaPaginaCanvas, canvas.height - origemY)
      );
      adicionarPagina(fatia);
    }
  }

  return pdf.output("blob");
}

const estadoInicial = {
  codigoBase: "",
  complementoCodigo: "",
  clienteId: "",
  obraId: "",
  data: new Date().toISOString().slice(0, 10),
  materialIds: [],
  ensaiosSolicitadosPorMaterial: {},
  anexos: [
    {
      id: "reacao-alcali-agregado",
      texto: "Ensaios de Reação Álcali Agregado",
      selecionado: false,
      tipoEnsaio: "raa",
      ensaioIds: [],
    },
    {
      id: "adesividade",
      texto: "Ensaios de Adesividade",
      selecionado: false,
      tipoEnsaio: "adesividade",
      ensaioIds: [],
    },
    {
      id: "mescla-granulometrica-cbuq",
      texto: "Mescla granulométrica para CBUQ (CAUQ) conforme DNIT 031/2024",
      selecionado: false,
      tipoEnsaio: "mescla-granulometrica-cbuq",
      ensaioIds: [],
    },
  ],
};

function criarEstadoInicial() {
  return {
    ...estadoInicial,
    materialIds: [],
    ensaiosSolicitadosPorMaterial: {},
    anexos: estadoInicial.anexos.map((anexo) => ({ ...anexo })),
  };
}

function formatarDataBR(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  if (data?.seconds) return new Date(data.seconds * 1000).toLocaleDateString("pt-BR");
  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");
  return String(data);
}

function resumoMaterial(item) {
  return {
    id: item.id,
    codigo: item.codigoFornecedor || item.fornecedorCodigo || item.codigoMaterial || item.codigo || "",
    descricao: item.amostra || item.descricao || item.Descricao || "",
    fornecedor: item.procedencia || item.fornecedor || item.Fornecedor || "",
    localColeta: item.localColeta || "",
    procedencia: item.procedencia || item.fornecedor || item.Fornecedor || "",
    tipoAgregado: item.tipoAgregado || "graudo",
  };
}

function codigoMaterialCaracterizacao(item) {
  return item?.codigoFornecedor || item?.fornecedorCodigo || item?.codigoMaterial || item?.codigo || "";
}

function rotuloMaterialCaracterizacao(item) {
  if (!item) return "";
  return `${codigoMaterialCaracterizacao(item)} - ${item.amostra || ""} - ${
    item.procedencia || item.fornecedor || item.Fornecedor || ""
  } - ${item.tipoAgregado === "miudo" ? "Miúdo" : "Graúdo"}`;
}

function normalizarPesquisaMaterial(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function montarCodigoRelatorio(base, complemento, dataRelatorio) {
  const baseNormalizada = String(Number(String(base || "").trim()));
  const complementoNormalizado = String(complemento || "").trim().toUpperCase();
  const anoRelatorio =
    String(dataRelatorio || "").match(/^(\d{4})-/)?.[1] ||
    String(new Date().getFullYear());
  const codigoBase = complementoNormalizado
    ? `${baseNormalizada}.${complementoNormalizado}`
    : baseNormalizada;
  return `RE-CA-${codigoBase}/${anoRelatorio}`;
}

function separarCodigoRelatorio(relatorio) {
  const codigoSalvo = String(relatorio?.codigoRelatorio || "").trim();
  const codigoLegado = codigoSalvo.match(/^RE-CA-(\d+)(?:\.([A-Z0-9]+))?\/\d{4}$/i);
  if (codigoLegado) {
    return {
      base: codigoLegado[1],
      complemento: codigoLegado[2] || "",
    };
  }

  const [base = "", ...complemento] = codigoSalvo.split(".");
  return {
    base: String(relatorio?.codigoBase || relatorio?.numeroRelatorio || base || ""),
    complemento: String(relatorio?.complementoCodigo || complemento.join(".") || ""),
  };
}

function exibirNumeroCodigoRelatorio(relatorio) {
  const codigo = separarCodigoRelatorio(relatorio);
  if (!codigo.base) return "-";
  return codigo.complemento
    ? `${codigo.base}.${codigo.complemento}`
    : codigo.base;
}

export default function RelatorioCaracterizacao() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const location = useLocation();
  const preSelecaoProcessadaRef = useRef(false);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [caracterizacoes, setCaracterizacoes] = useState([]);
  const [ensaiosRAA, setEnsaiosRAA] = useState([]);
  const [ensaiosAdesividade, setEnsaiosAdesividade] = useState([]);
  const [ensaiosMesclaGranulometrica, setEnsaiosMesclaGranulometrica] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [form, setForm] = useState(estadoInicial);
  const [modoPrint, setModoPrint] = useState(false);
  const [relatorioPrint, setRelatorioPrint] = useState(null);
  const [caracterizacoesPrint, setCaracterizacoesPrint] = useState([]);
  const [anexosEnsaiosPrint, setAnexosEnsaiosPrint] = useState([]);
  const [autoPrint, setAutoPrint] = useState(false);
  const [relatorioEditando, setRelatorioEditando] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [novoAnexo, setNovoAnexo] = useState("");
  const [dadosCarregados, setDadosCarregados] = useState(false);
  const [voltarParaOrigem, setVoltarParaOrigem] = useState(false);
  const [preparandoPrevia, setPreparandoPrevia] = useState(() =>
    Boolean(location.state?.origemOS || location.state?.origemListaCaracterizacao)
  );

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    const materialId = location.state?.materialId;
    if (preSelecaoProcessadaRef.current || !materialId || !dadosCarregados) return;

    const material = caracterizacoes.find((item) => item.id === materialId);
    if (!material) return;

    if (location.state?.origemOS || location.state?.origemListaCaracterizacao) {
      const raaIds = Array.isArray(location.state.raaIds) ? location.state.raaIds : [];
      const ensaiosCaracterizacaoIds = Array.isArray(location.state.ensaiosCaracterizacaoIds)
        ? location.state.ensaiosCaracterizacaoIds
        : [];
      preSelecaoProcessadaRef.current = true;
      history.replace(location.pathname);
      abrirPreviaMaterialOS(material, raaIds, ensaiosCaracterizacaoIds);
      return;
    }

    if (location.state?.abrirPrevia) {
      const relatorioSalvo = relatorios.find((relatorio) =>
        Array.isArray(relatorio.materialIds) && relatorio.materialIds.includes(material.id)
      );

      if (relatorioSalvo) {
        preSelecaoProcessadaRef.current = true;
        history.replace(location.pathname);
        setVoltarParaOrigem(true);
        abrirRelatorioSalvo(relatorioSalvo, false);
        return;
      }
    }

    const obra = obras.find((item) => item.id === material.obraId);
    const cliente = clientes.find(
      (item) =>
        item.id === material.clienteId ||
        String(item.nome || "").trim().toUpperCase() ===
          String(material.clienteNome || material.cliente || "").trim().toUpperCase()
    );
    const codigoClone = obterCodigoRelatorioClone(material);

    preSelecaoProcessadaRef.current = true;
    setRelatorioEditando(null);
    setForm({
      ...criarEstadoInicial(),
      codigoBase: codigoClone.base,
      complementoCodigo: codigoClone.complemento,
      clienteId: material.clienteId || obra?.clienteId || cliente?.id || "",
      obraId: material.obraId || obra?.id || "",
      materialIds: [material.id],
      data: new Date().toISOString().slice(0, 10),
    });
    setNovoAnexo("");
    setOpenDialog(true);
    history.replace(location.pathname);
  }, [caracterizacoes, clientes, dadosCarregados, history, location.pathname, location.state, obras, relatorios]);

  async function carregarDados() {
    const [
      clientesSnap,
      obrasSnap,
      caracterizacoesSnap,
      relatoriosSnap,
      ensaiosRAASnap,
      ensaiosAdesividadeSnap,
      ensaiosMesclaGranulometricaSnap,
    ] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "obras"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblCaracterizacaoAgregados"), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblRelatoriosCaracterizacaoAgregados"), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(collection(db, "tblEnsaioRAA"))),
      getDocs(consultaTenantAtual(query(collection(db, "tblAdesividadeLiganteBetuminoso"), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, COLECAO_GRANULOMETRIA_ASFALTO), orderBy("criadoEm", "desc")))),
    ]);

    setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCaracterizacoes(caracterizacoesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRelatorios(relatoriosSnap.docs.map((item) => ({ ...item.data(), id: item.id })));
    setEnsaiosRAA(ensaiosRAASnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setEnsaiosAdesividade(ensaiosAdesividadeSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setEnsaiosMesclaGranulometrica(
      ensaiosMesclaGranulometricaSnap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => item.tipo === "mescla")
    );
    setDadosCarregados(true);
  }

  const clienteSelecionado = clientes.find((cliente) => cliente.id === form.clienteId) || null;
  const obraSelecionada = obras.find((obra) => obra.id === form.obraId) || null;

  const obrasDoCliente = useMemo(
    () => obras.filter((obra) => !form.clienteId || obra.clienteId === form.clienteId),
    [obras, form.clienteId]
  );

  const materiaisDaObra = useMemo(() => {
    const materiaisUnicos = new Map();
    const selecionados = new Set(form.materialIds);

    caracterizacoes
      .filter((item) => !form.obraId || item.obraId === form.obraId)
      .forEach((item) => {
        const chave = normalizarPesquisaMaterial(rotuloMaterialCaracterizacao(item));
        const existente = materiaisUnicos.get(chave);
        if (!existente || selecionados.has(item.id)) {
          materiaisUnicos.set(chave, item);
        }
      });

    return Array.from(materiaisUnicos.values());
  }, [caracterizacoes, form.materialIds, form.obraId]);

  const materiaisSelecionados = useMemo(
    () => caracterizacoes.filter((item) => form.materialIds.includes(item.id)),
    [caracterizacoes, form.materialIds]
  );

  const ensaiosRAADaObra = useMemo(() => {
    const materialIdsSelecionados = new Set(
      materiaisSelecionados.map((material) => material.fornecedorId).filter(Boolean)
    );

    return ensaiosRAA.filter((ensaio) => {
      if (ensaio.cancelado === true) return false;
      const pertenceObra = !form.obraId || ensaio.obraId === form.obraId;
      const materialRAAId = ensaio.agregadoId || ensaio.fornecedorId;
      return pertenceObra && materialRAAId && materialIdsSelecionados.has(materialRAAId);
    });
  }, [ensaiosRAA, form.obraId, materiaisSelecionados]);

  const ensaiosAdesividadeDaObra = useMemo(
    () => ensaiosAdesividade.filter((ensaio) => !form.obraId || ensaio.obraId === form.obraId),
    [ensaiosAdesividade, form.obraId]
  );

  const ensaiosMesclaGranulometricaDaObra = useMemo(
    () => {
      const nomeObra = String(obraSelecionada?.nome || "").trim().toLowerCase();
      return ensaiosMesclaGranulometrica.filter((ensaio) => {
        if (!form.obraId) return true;
        if (ensaio.obraId) return ensaio.obraId === form.obraId;
        return nomeObra && String(ensaio.obra || "").trim().toLowerCase() === nomeObra;
      });
    },
    [ensaiosMesclaGranulometrica, form.obraId, obraSelecionada]
  );

  function materiaisResumoRelatorio(relatorio) {
    if (Array.isArray(relatorio.materiaisResumo) && relatorio.materiaisResumo.length > 0) {
      return relatorio.materiaisResumo;
    }

    const ids = Array.isArray(relatorio.materialIds) ? relatorio.materialIds : [];
    return ids
      .map((id) => caracterizacoes.find((item) => item.id === id))
      .filter(Boolean)
      .map(resumoMaterial);
  }

  function selecionarCliente(cliente) {
    setForm({
      ...form,
      clienteId: cliente?.id || "",
      obraId: "",
      materialIds: [],
    });
  }

  function selecionarObra(obra) {
    setForm({
      ...form,
      obraId: obra?.id || "",
      materialIds: [],
      anexos: form.anexos.map((anexo) => ({ ...anexo, ensaioIds: [] })),
    });
  }

  function obterCodigoRelatorioClone(material) {
    let caracterizacaoOrigemId = material?.clonadoDeId || "";

    if (!caracterizacaoOrigemId) {
      const codigoCaracterizacaoClone = String(material?.codigo || "").trim().match(/^(\d+)\.B$/i);
      const caracterizacaoOrigem = codigoCaracterizacaoClone
        ? caracterizacoes.find(
            (item) =>
              item.id !== material?.id &&
              String(item.codigo || "").trim() === codigoCaracterizacaoClone[1]
          )
        : null;
      caracterizacaoOrigemId = caracterizacaoOrigem?.id || "";
    }

    if (!caracterizacaoOrigemId) return { base: "", complemento: "" };

    const relatorioOrigem = relatorios.find((relatorio) =>
      Array.isArray(relatorio.materialIds) && relatorio.materialIds.includes(caracterizacaoOrigemId)
    );
    const codigoOrigem = separarCodigoRelatorio(relatorioOrigem);

    return codigoOrigem.base
      ? { base: codigoOrigem.base, complemento: "B" }
      : { base: "", complemento: "" };
  }

  function selecionarMateriais(materiais) {
    const materialIds = materiais.map((item) => item.id);
    const codigoClone = materiais.length === 1
      ? obterCodigoRelatorioClone(materiais[0])
      : { base: "", complemento: "" };
    const materiaisCadastradosIds = new Set(materiais.map((item) => item.fornecedorId).filter(Boolean));
    const ensaiosRAAPermitidos = new Set(
      ensaiosRAA
        .filter(
          (ensaio) =>
            ensaio.cancelado !== true &&
            materiaisCadastradosIds.has(ensaio.agregadoId || ensaio.fornecedorId)
        )
        .map((ensaio) => ensaio.id)
    );

    setForm((prev) => ({
      ...prev,
      ...(codigoClone.base && !relatorioEditando
        ? { codigoBase: codigoClone.base, complementoCodigo: codigoClone.complemento }
        : {}),
      materialIds,
      anexos: prev.anexos.map((anexo) =>
        anexo.tipoEnsaio === "raa"
          ? {
              ...anexo,
              ensaioIds: (anexo.ensaioIds || []).filter((id) => ensaiosRAAPermitidos.has(id)),
            }
          : anexo
      ),
    }));
  }

  function rotuloEnsaioRAA(ensaio) {
    const materialRAAId = ensaio.agregadoId || ensaio.fornecedorId;
    const caracterizacao = materiaisSelecionados.find(
      (material) => material.fornecedorId === materialRAAId
    );
    const codigoMaterial = codigoMaterialCaracterizacao(caracterizacao);
    const descricao = caracterizacao?.amostra || ensaio.agregado || ensaio.fornecedor || "Agregado";

    return `${codigoMaterial || "-"} - ${descricao} - Ensaio RAA ${ensaio.codigo || "-"}`;
  }

  function novoRelatorio() {
    setRelatorioEditando(null);
    setForm({
      ...criarEstadoInicial(),
      data: new Date().toISOString().slice(0, 10),
    });
    setNovoAnexo("");
    setOpenDialog(true);
  }

  function cancelarRelatorio() {
    setRelatorioEditando(null);
    setForm({
      ...criarEstadoInicial(),
      data: new Date().toISOString().slice(0, 10),
    });
    setNovoAnexo("");
    setOpenDialog(false);
  }

  function editarRelatorio(relatorio) {
    const codigoAtual = separarCodigoRelatorio(relatorio);
    const anexosSalvos = Array.isArray(relatorio.anexos) ? relatorio.anexos : [];
    const anexos = estadoInicial.anexos.map((anexoPadrao) => {
      const salvo = anexosSalvos.find((anexo) => anexo.id === anexoPadrao.id);
      return salvo ? { ...anexoPadrao, ...salvo } : { ...anexoPadrao };
    });
    anexosSalvos
      .filter((anexo) => !anexos.some((item) => item.id === anexo.id))
      .forEach((anexo) => anexos.push({ ...anexo }));

    setRelatorioEditando(relatorio);
    setForm({
      codigoBase: codigoAtual.base,
      complementoCodigo: codigoAtual.complemento,
      clienteId: relatorio.clienteId || "",
      obraId: relatorio.obraId || "",
      data: relatorio.data || new Date().toISOString().slice(0, 10),
      materialIds: Array.isArray(relatorio.materialIds) ? relatorio.materialIds : [],
      ensaiosSolicitadosPorMaterial: relatorio.ensaiosSolicitadosPorMaterial || {},
      anexos,
    });
    setNovoAnexo("");
    setOpenDialog(true);
  }

  function alternarAnexo(id, selecionado) {
    setForm((prev) => ({
      ...prev,
      anexos: prev.anexos.map((anexo) =>
        anexo.id === id
          ? { ...anexo, selecionado, ensaioIds: selecionado ? anexo.ensaioIds || [] : [] }
          : anexo
      ),
    }));
  }

  function selecionarEnsaiosAnexo(id, ensaioIds) {
    setForm((prev) => ({
      ...prev,
      anexos: prev.anexos.map((anexo) => (anexo.id === id ? { ...anexo, ensaioIds } : anexo)),
    }));
  }

  function adicionarAnexo() {
    const texto = novoAnexo.trim();
    if (!texto) return;

    setForm((prev) => ({
      ...prev,
      anexos: [
        ...prev.anexos,
        {
          id: `personalizado-${Date.now()}`,
          texto,
          selecionado: true,
          personalizado: true,
          ensaioIds: [],
        },
      ],
    }));
    setNovoAnexo("");
  }

  function removerAnexo(id) {
    setForm((prev) => ({
      ...prev,
      anexos: prev.anexos.filter((anexo) => anexo.id !== id),
    }));
  }

  async function salvarRelatorio({ somenteVisualizar = false } = {}) {
    if (!form.clienteId) {
      alert("Selecione o cliente.");
      return null;
    }

    if (!form.obraId) {
      alert("Selecione a obra.");
      return null;
    }

    if (materiaisSelecionados.length === 0) {
      alert("Selecione pelo menos um material.");
      return null;
    }

    const anexoSemEnsaio = form.anexos.find(
      (anexo) =>
        anexo.selecionado &&
        anexo.tipoEnsaio &&
        (!Array.isArray(anexo.ensaioIds) || anexo.ensaioIds.length === 0)
    );
    if (anexoSemEnsaio) {
      alert(`Selecione pelo menos um ensaio para o anexo "${anexoSemEnsaio.texto}".`);
      return null;
    }

    const codigoBaseInformado = String(form.codigoBase || "").trim();
    const complementoCodigo = String(form.complementoCodigo || "").trim().toUpperCase();

    if (codigoBaseInformado && !/^\d+$/.test(codigoBaseInformado)) {
      alert("O código-base deve conter somente números.");
      return null;
    }

    if (complementoCodigo && !/^[A-Z0-9]{1,5}$/.test(complementoCodigo)) {
      alert("O complemento deve conter até 5 letras ou números, sem pontos ou espaços.");
      return null;
    }

    const relatoriosAtuaisSnap = await getDocs(
      consultaTenantAtual(collection(db, "tblRelatoriosCaracterizacaoAgregados"))
    );

    let codigoBase = codigoBaseInformado;
    if (!codigoBase && !somenteVisualizar) {
      const maiorNumeroExistente = relatoriosAtuaisSnap.docs.reduce((maior, item) => {
        const codigoAtual = separarCodigoRelatorio({ id: item.id, ...item.data() });
        const numero = Number(codigoAtual.base || 0);
        return Number.isFinite(numero) && numero > maior ? numero : maior;
      }, 0);
      const sequencial = await gerarSequencial({
        chave: "relatorio_caracterizacao",
        valorMinimo: maiorNumeroExistente,
      });
      codigoBase = String(sequencial.numero);
    }

    const numeroRelatorio = Number(codigoBase);
    const codigoOriginal = relatorioEditando ? separarCodigoRelatorio(relatorioEditando) : null;
    const codigoFoiAlterado =
      !codigoOriginal ||
      Number(codigoOriginal.base) !== numeroRelatorio ||
      String(codigoOriginal.complemento || "").toUpperCase() !== complementoCodigo;
    const codigoRelatorio =
      relatorioEditando && !codigoFoiAlterado
        ? relatorioEditando.codigoRelatorio
        : codigoBase
          ? montarCodigoRelatorio(codigoBase, complementoCodigo, form.data)
          : "";
    const codigoDuplicado = Boolean(codigoBase) && codigoFoiAlterado && relatoriosAtuaisSnap.docs.some((item) => {
      if (item.id === relatorioEditando?.id) return false;

      const relatorioExistente = { id: item.id, ...item.data() };
      const codigoExistente = separarCodigoRelatorio(relatorioExistente);
      const mesmaBase = Number(codigoExistente.base) === numeroRelatorio;

      if (!complementoCodigo) return mesmaBase;

      return (
        mesmaBase &&
        String(codigoExistente.complemento || "").toUpperCase() === complementoCodigo
      );
    });

    if (codigoDuplicado) {
      alert(`O código ${codigoRelatorio} já existe.`);
      return null;
    }

    const dados = aplicarTenantAtual({
      clienteId: form.clienteId,
      clienteNome: clienteSelecionado?.nome || "",
      obraId: form.obraId,
      obraNome: obraSelecionada?.nome || "",
      data: form.data || new Date().toISOString().slice(0, 10),
      materialIds: materiaisSelecionados.map((item) => item.id),
      materiaisResumo: materiaisSelecionados.map(resumoMaterial),
      ensaiosSolicitadosPorMaterial: form.ensaiosSolicitadosPorMaterial || {},
      numeroRelatorio,
      codigoRelatorio,
      codigoBase,
      complementoCodigo: complementoCodigo || null,
      anexos: form.anexos.map((anexo) => ({
        id: anexo.id,
        texto: anexo.texto,
        selecionado: Boolean(anexo.selecionado),
        ensaioIds: Array.isArray(anexo.ensaioIds) ? anexo.ensaioIds : [],
        ...(anexo.tipoEnsaio ? { tipoEnsaio: anexo.tipoEnsaio } : {}),
        ...(anexo.personalizado ? { personalizado: true } : {}),
      })),
      atualizadoEm: new Date(),
    });

    if (somenteVisualizar) return dados;

    let salvo;

    if (relatorioEditando?.id) {
      await updateDoc(doc(db, "tblRelatoriosCaracterizacaoAgregados", relatorioEditando.id), dados);
      salvo = { ...relatorioEditando, ...dados };
    } else {
      const ref = await addDoc(collection(db, "tblRelatoriosCaracterizacaoAgregados"), {
        ...dados,
        criadoEm: new Date(),
      });
      salvo = { id: ref.id, ...dados };
    }

    await carregarDados();
    if (relatorioEditando?.id) {
      setRelatorios((anteriores) =>
        anteriores.map((item) => item.id === salvo.id ? salvo : item)
      );
    }
    cancelarRelatorio();
    return salvo;
  }

  async function gerarRelatorio() {
    const previa = await salvarRelatorio({ somenteVisualizar: true });
    if (!previa) return;

    setRelatorioPrint(previa);
    setCaracterizacoesPrint(materiaisSelecionados);
    setAnexosEnsaiosPrint(await carregarEnsaiosAnexos(previa));
    setAutoPrint(false);
    setOpenDialog(false);
    setModoPrint(true);
  }

  async function abrirRelatorioSalvo(relatorio, imprimir = false) {
    const ids = Array.isArray(relatorio.materialIds) ? relatorio.materialIds : [];
    const materiais = await Promise.all(
      ids.map(async (id) => {
        const snap = await getDoc(doc(db, "tblCaracterizacaoAgregados", id));
        return snap.exists()
          ? {
              id: snap.id,
              ...snap.data(),
              ensaiosSolicitados: relatorio.ensaiosSolicitadosPorMaterial?.[id] || [],
            }
          : null;
      })
    );

    setRelatorioPrint(relatorio);
    setCaracterizacoesPrint(materiais.filter(Boolean));
    setAnexosEnsaiosPrint(await carregarEnsaiosAnexos(relatorio));
    setAutoPrint(imprimir);
    setModoPrint(true);
  }

  async function abrirPreviaMaterialOS(material, raaIdsOS = [], ensaiosCaracterizacaoIds = []) {
    const relatorioSalvo = relatorios.find((relatorio) =>
      Array.isArray(relatorio.materialIds) && relatorio.materialIds.includes(material.id)
    );
    if (relatorioSalvo) {
      await abrirRelatorioSalvo(relatorioSalvo, false);
      setVoltarParaOrigem(true);
      setPreparandoPrevia(false);
      return;
    }
    const codigoClone = obterCodigoRelatorioClone(material);
    const materialCadastradoId = material.fornecedorId;
    const idsRAAInformados = new Set(raaIdsOS);
    const ensaiosRAARelacionados = ensaiosRAA.filter((ensaio) => {
      if (ensaio.cancelado === true) return false;
      if (idsRAAInformados.size > 0) return idsRAAInformados.has(ensaio.id);
      const materialRAAId = ensaio.agregadoId || ensaio.fornecedorId;
      const mesmaObra = !material.obraId || !ensaio.obraId || ensaio.obraId === material.obraId;
      return materialCadastradoId && materialRAAId === materialCadastradoId && mesmaObra;
    });
    const anexos = criarEstadoInicial().anexos.map((anexo) =>
      anexo.tipoEnsaio === "raa"
        ? {
            ...anexo,
            selecionado: ensaiosRAARelacionados.length > 0,
            ensaioIds: ensaiosRAARelacionados.map((ensaio) => ensaio.id),
          }
        : anexo
    );
    let ensaiosCaracterizacaoSelecionados = ensaiosCaracterizacaoIds;
    if (ensaiosCaracterizacaoSelecionados.length === 0 && material.solicitacaoId) {
      const solicitacaoSnap = await getDoc(doc(db, "tblSolicitacoesServicos", material.solicitacaoId));
      if (solicitacaoSnap.exists()) {
        ensaiosCaracterizacaoSelecionados = (solicitacaoSnap.data().ensaios || [])
          .filter((ensaio) => String(ensaio.id || "").startsWith("caracterizacao-"))
          .map((ensaio) => ensaio.id);
      }
    }
    const ensaiosSolicitados = ensaiosCaracterizacaoSelecionados.length > 0
      ? ensaiosCaracterizacaoSelecionados
      : relatorioSalvo?.ensaiosSolicitadosPorMaterial?.[material.id] || [];
    const materialPrevia = {
      ...material,
      ensaiosSolicitados,
    };
    const relatorioPrevia = {
      ...(relatorioSalvo || {}),
      codigoBase: codigoClone.base,
      complementoCodigo: codigoClone.complemento || null,
      numeroRelatorio: codigoClone.base ? Number(codigoClone.base) : null,
      codigoRelatorio: codigoClone.base
        ? montarCodigoRelatorio(codigoClone.base, codigoClone.complemento, material.dataCadastro)
        : "",
      clienteId: material.clienteId || relatorioSalvo?.clienteId || "",
      clienteNome: material.clienteNome || material.cliente || relatorioSalvo?.clienteNome || "",
      obraId: material.obraId || relatorioSalvo?.obraId || "",
      obraNome: material.obraNome || material.obra || relatorioSalvo?.obraNome || "",
      data:
        relatorioSalvo?.data ||
        material.dataCadastro ||
        material.dataGranulometria ||
        new Date().toISOString().slice(0, 10),
      materialIds: [material.id],
      materiaisResumo: [resumoMaterial(material)],
      ensaiosSolicitadosPorMaterial: {
        [material.id]: ensaiosSolicitados,
      },
      anexos,
    };

    const obraPrevia = obras.find(
      (obra) =>
        obra.id === material.obraId ||
        String(obra.nome || "").trim().toUpperCase() ===
          String(material.obraNome || material.obra || "").trim().toUpperCase()
    );
    const clientePrevia = clientes.find(
      (cliente) =>
        cliente.id === material.clienteId ||
        String(cliente.nome || "").trim().toUpperCase() ===
          String(material.clienteNome || material.cliente || "").trim().toUpperCase()
    );

    if (!relatorioSalvo) {
      setRelatorioEditando(null);
      setForm({
        ...criarEstadoInicial(),
        codigoBase: codigoClone.base,
        complementoCodigo: codigoClone.complemento,
        clienteId: material.clienteId || obraPrevia?.clienteId || clientePrevia?.id || "",
        obraId: material.obraId || obraPrevia?.id || "",
        data: relatorioPrevia.data,
        materialIds: [material.id],
        ensaiosSolicitadosPorMaterial: {
          [material.id]: ensaiosSolicitados,
        },
        anexos,
      });
    }

    setRelatorioPrint(relatorioPrevia);
    setCaracterizacoesPrint([materialPrevia]);
    setAnexosEnsaiosPrint(await carregarEnsaiosAnexos(relatorioPrevia));
    setAutoPrint(false);
    setVoltarParaOrigem(true);
    setModoPrint(true);
    setPreparandoPrevia(false);
  }

  async function vincularRelatorioAosLaudos(relatorio) {
    if (!relatorio?.obraId) {
      throw new Error("Não foi possível identificar a obra do relatório.");
    }
    if (!auth.currentUser) {
      throw new Error("Usuário não autenticado.");
    }

    const laudosSnap = await getDocs(
      consultaTenantAtual(
        query(collection(db, "laudos"), where("obraId", "==", relatorio.obraId))
      )
    );
    const revisoesSalvas = laudosSnap.docs
      .map((item) => item.data())
      .filter((item) => item.relatorioCaracterizacaoId === relatorio.id)
      .sort((a, b) => Number(b.numeroRevisao || 0) - Number(a.numeroRevisao || 0));
    const ultimaRevisao = revisoesSalvas[0];
    const numeroRevisao = ultimaRevisao ? Number(ultimaRevisao.numeroRevisao || 0) + 1 : 0;

    const codigo = relatorio.codigoRelatorio || `RE-CA-${relatorio.numeroRelatorio || ""}`;
    const materiaisResumo = materiaisResumoRelatorio(relatorio).map((material) => {
      const cadastro = caracterizacoesPrint.find((item) => item.id === material.id);
      return {
        ...material,
        localColeta: material.localColeta ?? cadastro?.localColeta ?? "",
        procedencia: material.procedencia ?? cadastro?.procedencia ?? material.fornecedor ?? "",
      };
    });
    const identificacaoMateriais = materiaisResumo.map((material) => [
      material.descricao || "-",
      material.procedencia || material.fornecedor || "-",
      material.localColeta || "-",
    ].join(" - ")).join("; ");
    const descricao = [`${codigo} rev.${numeroRevisao}`, identificacaoMateriais].filter(Boolean).join(" - ");
    const nomeArquivo = `${nomeSeguroArquivo(descricao)}.pdf`;
    const storagePath = `laudos/${relatorio.obraId}/${Date.now()}-${nomeArquivo}`;
    setRelatorioPrint({ ...relatorio, numeroRevisao });
    await new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
    const pdf = await gerarPdfRelatorioCaracterizacao();

    await uploadBytes(ref(storage, storagePath), pdf, { contentType: "application/pdf" });

    const laudoRef = await addDoc(collection(db, "laudos"), aplicarTenantAtual({
      obraId: relatorio.obraId,
      tipo: TIPO_LAUDO_CARACTERIZACAO,
      descricao,
      nomeArquivo,
      storagePath,
      relatorioCaracterizacaoId: relatorio.id,
      numeroRevisao,
      identificacaoMateriais,
      materiaisResumo,
      cancelado: false,
      criadoPor: auth.currentUser.uid,
      criadoPorEmail: auth.currentUser.email || "",
      criadoEm: serverTimestamp(),
    }));

    return { laudoId: laudoRef.id, numeroRevisao, materiaisResumo };
  }

  async function salvarPreviaAtual() {
    try {
      const salvo = await executarSalvamento(async () => {
        const relatorioSalvo = relatorioPrint.id ? relatorioPrint : await salvarRelatorio();
        if (!relatorioSalvo) return null;

        const revisaoSalva = await vincularRelatorioAosLaudos(relatorioSalvo);
        await updateDoc(doc(db, "tblRelatoriosCaracterizacaoAgregados", relatorioSalvo.id), {
          ...revisaoSalva,
          atualizadoEm: new Date(),
        });

        return { ...relatorioSalvo, ...revisaoSalva };
      });

      if (salvo) {
        setRelatorioPrint(salvo);
        setRelatorios((anteriores) => anteriores.map((item) => item.id === salvo.id ? salvo : item));
        alert("Relatório salvo com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao vincular relatório de caracterização aos laudos:", error);
      alert("Não foi possível vincular o relatório aos laudos. Tente novamente.");
    }
  }

  async function carregarEnsaiosAnexos(relatorio) {
    const anexos = Array.isArray(relatorio?.anexos) ? relatorio.anexos : [];
    const selecionados = anexos.filter((anexo) => anexo.selecionado && anexo.tipoEnsaio);
    const resultados = [];

    for (const anexo of selecionados) {
      for (const ensaioId of anexo.ensaioIds || []) {
        if (anexo.tipoEnsaio === "raa") {
          const ensaio = ensaiosRAA.find((item) => item.id === ensaioId);
          if (!ensaio || ensaio.cancelado === true) continue;
          const leiturasSnap = await getDocs(collection(db, "tblEnsaioRAA", ensaioId, "leituras"));
          const leituras = leiturasSnap.docs.map((item) => {
            const leitura = item.data();
            return {
              id: item.id,
              dataLeitura: leitura.data,
              barra106: { expansao: leitura.barras?.[0]?.expansao },
              barra107: { expansao: leitura.barras?.[1]?.expansao },
              barra108: { expansao: leitura.barras?.[2]?.expansao },
              expansaoMedia:
                leitura.media !== "" && leitura.media !== null && leitura.media !== undefined
                  ? Number(String(leitura.media).replace(",", "."))
                  : null,
            };
          });
          resultados.push({
            tipo: "raa",
            anexoId: anexo.id,
            anexoTexto: anexo.texto,
            ensaio,
            leituras,
          });
        }

        if (anexo.tipoEnsaio === "adesividade") {
          const ensaio = ensaiosAdesividade.find((item) => item.id === ensaioId);
          if (ensaio) {
            resultados.push({
              tipo: "adesividade",
              anexoId: anexo.id,
              anexoTexto: anexo.texto,
              ensaio,
            });
          }
        }

        if (anexo.tipoEnsaio === "mescla-granulometrica-cbuq") {
          const ensaio = ensaiosMesclaGranulometrica.find((item) => item.id === ensaioId);
          if (ensaio) {
            resultados.push({
              tipo: "mescla-granulometrica-cbuq",
              anexoId: anexo.id,
              anexoTexto: anexo.texto,
              ensaio,
            });
          }
        }
      }
    }

    return resultados;
  }

  if (preparandoPrevia) {
    return (
      <>
        <Navbar />
        <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress />
            <Typography color="text.secondary">Preparando prévia do relatório...</Typography>
          </Stack>
        </Box>
      </>
    );
  }

  if (modoPrint && relatorioPrint) {
    const clientePrint =
      clientes.find((cliente) => cliente.id === relatorioPrint.clienteId) ||
      { nome: relatorioPrint.clienteNome };
    const obraPrint =
      obras.find((obra) => obra.id === relatorioPrint.obraId) ||
      { nome: relatorioPrint.obraNome };

    return (
      <RelatorioCaracterizacaoPrint
        relatorio={relatorioPrint}
        caracterizacoes={caracterizacoesPrint}
        anexosEnsaios={anexosEnsaiosPrint}
        cliente={clientePrint}
        obra={obraPrint}
        autoPrint={autoPrint}
        onSalvar={salvarPreviaAtual}
        salvando={salvandoCadastro}
        onVoltar={() => {
          if (voltarParaOrigem) {
            history.goBack();
            return;
          }
          setModoPrint(false);
          if (!relatorioPrint.id) setOpenDialog(true);
          setRelatorioPrint(null);
          setCaracterizacoesPrint([]);
          setAnexosEnsaiosPrint([]);
          setAutoPrint(false);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ mt: "10px", textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700}>Relatório de Caracterização</Typography>
        <Typography variant="body2">Geração de relatório por cliente, obra e materiais caracterizados</Typography>
      </Box>

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <Button variant="outlined" onClick={() => history.push("/app/cadastro-caracterizacao")}>
            Voltar
          </Button>
          <Button variant="contained" onClick={novoRelatorio}>Novo Relatório</Button>
        </Box>

        <Paper sx={{ borderRadius: "8px", overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <Table size="small" sx={{ minWidth: 850 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Materiais</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {relatorios.map((relatorio) => (
                <TableRow key={relatorio.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {exibirNumeroCodigoRelatorio(relatorio)}
                  </TableCell>
                  <TableCell>{formatarDataBR(relatorio.data)}</TableCell>
                  <TableCell sx={{ minWidth: 360 }}>
                    <Typography sx={{ fontSize: "0.86rem", color: "#001f3f" }}>
                      {relatorio.clienteNome || "-"}
                    </Typography>
                    <Box sx={{ borderTop: "1px solid #d7dde6", mt: 1, pt: 1 }}>
                      <Typography sx={{ fontSize: "0.78rem", color: "#001f3f", whiteSpace: "nowrap" }}>
                        Obra: {relatorio.obraNome || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 560, whiteSpace: "nowrap" }}>
                    {materiaisResumoRelatorio(relatorio).map((material) => (
                      <Box
                        key={material.id}
                        sx={{
                          borderBottom: "1px solid #e2e8f0",
                          py: 0.5,
                          "&:last-child": { borderBottom: 0 },
                        }}
                      >
                        <Typography sx={{ fontSize: "0.82rem", color: "#334155", whiteSpace: "nowrap" }}>
                          {material.codigo || "-"} - {material.descricao || "-"} - Fornecedor: {material.fornecedor || "-"}
                        </Typography>
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => editarRelatorio(relatorio)}>
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={() => abrirRelatorioSalvo(relatorio, false)}>
                        <VisibilityIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {relatorios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">Nenhum relatório salvo.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && cancelarRelatorio()} fullWidth maxWidth="md">
        <DialogTitle>{relatorioEditando ? "Editar Relatório" : "Novo Relatório"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Código-base do relatório"
                value={form.codigoBase || ""}
                onChange={(event) =>
                  setForm({
                    ...form,
                    codigoBase: event.target.value.replace(/\D/g, ""),
                  })
                }
                placeholder="Automático"
                helperText="Deixe vazio para gerar automaticamente ou informe somente o número-base."
                inputProps={{ inputMode: "numeric" }}
                fullWidth
              />
              <TextField
                label="Complemento do código (opcional)"
                value={form.complementoCodigo || ""}
                onChange={(event) =>
                  setForm({
                    ...form,
                    complementoCodigo: event.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 5),
                  })
                }
                placeholder="Ex.: B"
                helperText={
                  form.codigoBase
                    ? `Código final: ${montarCodigoRelatorio(form.codigoBase, form.complementoCodigo, form.data)}`
                    : form.complementoCodigo
                      ? `A base será gerada automaticamente com o complemento ${form.complementoCodigo}.`
                      : "Use letras ou números, sem digitar o ponto."
                }
                inputProps={{ maxLength: 5 }}
                fullWidth
              />
            </Stack>

            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 180px" }} gap={2}>
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) => option?.nome || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={clienteSelecionado}
                onChange={(event, value) => selecionarCliente(value)}
                renderInput={(params) => <TextField {...params} label="Cliente" />}
              />
              <Autocomplete
                options={obrasDoCliente}
                getOptionLabel={(option) => option?.nome || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={obraSelecionada}
                onChange={(event, value) => selecionarObra(value)}
                renderInput={(params) => <TextField {...params} label="Obra" />}
                disabled={!form.clienteId}
              />
              <TextField
                label="Data"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.data}
                onChange={(event) => setForm({ ...form, data: event.target.value })}
              />
            </Box>

            <Autocomplete
              multiple
              options={materiaisDaObra}
              getOptionLabel={rotuloMaterialCaracterizacao}
              filterOptions={(options, state) => {
                const termos = normalizarPesquisaMaterial(state.inputValue)
                  .split(/\s+/)
                  .filter(Boolean);
                if (!termos.length) return options;

                return options.filter((option) => {
                  const texto = normalizarPesquisaMaterial(rotuloMaterialCaracterizacao(option));
                  return termos.every((termo) => texto.includes(termo));
                });
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={materiaisSelecionados}
              onChange={(event, value) => selecionarMateriais(value)}
              renderInput={(params) => (
                <TextField {...params} label="Materiais cadastrados" placeholder="Selecione os materiais" />
              )}
              disabled={!form.obraId}
            />

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700} mb={1}>
                Anexos do relatório
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Marque somente os anexos que devem aparecer antes das considerações finais.
              </Typography>

              <Stack spacing={0.5}>
                {form.anexos.map((anexo) => (
                  <Box key={anexo.id}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(anexo.selecionado)}
                            onChange={(event) => alternarAnexo(anexo.id, event.target.checked)}
                          />
                        }
                        label={anexo.texto}
                        sx={{ flex: 1, m: 0 }}
                      />
                      {anexo.personalizado && (
                        <Button color="error" size="small" onClick={() => removerAnexo(anexo.id)}>
                          Remover
                        </Button>
                      )}
                    </Box>

                    {anexo.selecionado && anexo.tipoEnsaio === "raa" && (
                      <Autocomplete
                        multiple
                        options={ensaiosRAADaObra}
                        getOptionLabel={rotuloEnsaioRAA}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={ensaiosRAADaObra.filter((ensaio) => (anexo.ensaioIds || []).includes(ensaio.id))}
                        onChange={(event, value) =>
                          selecionarEnsaiosAnexo(anexo.id, value.map((ensaio) => ensaio.id))
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Ensaios RAA que serão anexados" size="small" />
                        )}
                        sx={{ ml: 4, mt: 1, mb: 1 }}
                        disabled={!form.obraId}
                      />
                    )}

                    {anexo.selecionado && anexo.tipoEnsaio === "adesividade" && (
                      <Autocomplete
                        multiple
                        options={ensaiosAdesividadeDaObra}
                        getOptionLabel={(ensaio) =>
                          `${ensaio.codigo || "-"} - ${ensaio.material || "Ensaio de adesividade"}`
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={ensaiosAdesividadeDaObra.filter((ensaio) =>
                          (anexo.ensaioIds || []).includes(ensaio.id)
                        )}
                        onChange={(event, value) =>
                          selecionarEnsaiosAnexo(anexo.id, value.map((ensaio) => ensaio.id))
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Ensaios de adesividade que serão anexados" size="small" />
                        )}
                        sx={{ ml: 4, mt: 1, mb: 1 }}
                        disabled={!form.obraId}
                      />
                    )}

                    {anexo.selecionado && anexo.tipoEnsaio === "mescla-granulometrica-cbuq" && (
                      <Autocomplete
                        multiple
                        options={ensaiosMesclaGranulometricaDaObra}
                        getOptionLabel={(ensaio) =>
                          `${ensaio.codigo || "-"} - ${ensaio.material || "Mescla granulométrica"}`
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={ensaiosMesclaGranulometricaDaObra.filter((ensaio) =>
                          (anexo.ensaioIds || []).includes(ensaio.id)
                        )}
                        onChange={(event, value) =>
                          selecionarEnsaiosAnexo(anexo.id, value.map((ensaio) => ensaio.id))
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Mesclas granulométricas que serão anexadas" size="small" />
                        )}
                        sx={{ ml: 4, mt: 1, mb: 1 }}
                        disabled={!form.obraId}
                      />
                    )}
                  </Box>
                ))}
              </Stack>

              <Box display="flex" gap={1} mt={2}>
                <TextField
                  label="Novo item de anexo"
                  value={novoAnexo}
                  onChange={(event) => setNovoAnexo(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      adicionarAnexo();
                    }
                  }}
                  fullWidth
                  size="small"
                />
                <Button variant="outlined" onClick={adicionarAnexo} disabled={!novoAnexo.trim()}>
                  Adicionar
                </Button>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarRelatorio} disabled={salvandoCadastro}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => executarSalvamento(relatorioEditando ? salvarRelatorio : gerarRelatorio)}
            disabled={salvandoCadastro}
          >
            {salvandoCadastro ? (relatorioEditando ? "Salvando..." : "Preparando...") : relatorioEditando ? "Atualizar" : "Visualizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
